import { parseCookie, stringifySetCookie } from "cookie";
// src/preferences/index.ts
/**
* Browser preferences shared by every HLT surface. The apps run on separate origins and browser storage is per
* origin, so the preference lives in a first-party cookie on the registrable domain they share. Cookies ignore
* ports, so the local dev apps share it too. The API does not read it; `localStorage` keeps a per-origin copy
* for browsers that block cookies. The only dependency is the small `cookie` parser, so the public site's
* script can use it.
*/
const themeCookie = "hlt_theme";
const localeCookie = "hlt_locale";
const legacyKeys = {
	[themeCookie]: "hlt.theme",
	[localeCookie]: "hlt.locale"
};
const oneYear = 31536e3;
let sharedHosts = [];
/**
* Hostnames of every HLT app this bundle links to (site, platform, tools). Their common parent domain
* receives the preference cookie; without configuration the cookie stays on the current host.
*/
function configurePreferences(hosts) {
	sharedHosts = hosts.filter(Boolean);
	dropRetiredPreferences();
}
const isIp = (host) => /^[\d.]+$/.test(host) || host.includes(":");
/** The longest domain suffix (in whole labels) shared by the current host and every configured app host. */
function sharedCookieDomain(current, hosts) {
	if (!current || current === "localhost" || isIp(current) || !current.includes(".")) return null;
	let labels = current.split(".");
	for (const host of hosts) {
		if (!host || host === current) continue;
		const other = host.split(".");
		let common = 0;
		while (common < labels.length && common < other.length && labels[labels.length - 1 - common] === other[other.length - 1 - common]) common += 1;
		labels = labels.slice(labels.length - common);
	}
	return labels.length >= 2 ? labels.join(".") : null;
}
function readCookie(name) {
	if (typeof document === "undefined") return null;
	return parseCookie(document.cookie)[name] ?? null;
}
function writeCookie(name, value) {
	if (typeof document === "undefined") return;
	document.cookie = stringifySetCookie({
		name,
		value: value ?? "",
		path: "/",
		maxAge: value === null ? 0 : oneYear,
		sameSite: "lax",
		domain: sharedCookieDomain(window.location.hostname, sharedHosts) ?? void 0,
		secure: window.location.protocol === "https:"
	});
}
function readStorage(key) {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}
function writeStorage(key, value) {
	try {
		if (value === null) localStorage.removeItem(key);
		else localStorage.setItem(key, value);
	} catch {}
}
/** The explicit choice stored in the browser, or null when the person never chose. */
function readPreference(name) {
	const fromCookie = readCookie(name);
	if (fromCookie) return fromCookie;
	const legacy = readStorage(legacyKeys[name] ?? name);
	if (legacy && legacy !== "system") {
		writeCookie(name, legacy);
		return legacy;
	}
	return null;
}
function writePreference(name, value) {
	writeCookie(name, value);
	writeStorage(legacyKeys[name] ?? name, value);
}
/**
* The browser language is detected on every visit and never stored, so an old saved copy is deleted when present.
*/
function dropRetiredPreferences() {
	if (readCookie("hlt_locale_detected") !== null) writeCookie("hlt_locale_detected", null);
	if (readStorage("hlt.locale-detected") !== null) writeStorage("hlt.locale-detected", null);
}
function storedTheme() {
	const value = readPreference(themeCookie);
	return value === "light" || value === "dark" ? value : "system";
}
function systemTheme() {
	return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function resolveTheme(preference = storedTheme()) {
	return preference === "system" ? systemTheme() : preference;
}
/**
* Pin the document to a theme (or follow the system again) and remember it for every HLT app.
* `persist: false` only applies it, for example to mirror a change made in another tab.
*/
function applyTheme(preference, { persist = true } = {}) {
	const root = document.documentElement;
	if (preference === "system") delete root.dataset.theme;
	else root.dataset.theme = preference;
	root.setAttribute("data-mantine-color-scheme", resolveTheme(preference));
	if (persist) writePreference(themeCookie, preference === "system" ? null : preference);
}
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const revealProperties = [
	"--hlt-theme-x",
	"--hlt-theme-y",
	"--hlt-theme-r"
];
let revealing = false;
/**
* Switch to `next` with a reveal from `origin` (the toggle's center): a circular wipe where the View
* Transitions API exists, otherwise a 200 ms color cross-fade. Reduced motion, and a click during a running
* wipe, switch instantly.
*
* The wipe itself is a CSS animation (`base.css`) so it is attached in the same frame as the snapshot;
* starting it from script after `ready` showed one frame of the unclipped new theme.
*/
function transitionTheme(next, origin) {
	const root = document.documentElement;
	const doc = document;
	if (reducedMotion() || revealing) {
		applyTheme(next);
		return;
	}
	if (typeof doc.startViewTransition === "function") {
		const x = origin?.x ?? window.innerWidth / 2;
		const y = origin?.y ?? 0;
		const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
		const [xProperty, yProperty, radiusProperty] = revealProperties;
		root.style.setProperty(xProperty, `${x}px`);
		root.style.setProperty(yProperty, `${y}px`);
		root.style.setProperty(radiusProperty, `${Math.ceil(radius)}px`);
		root.dataset.themeTransition = "reveal";
		revealing = true;
		doc.startViewTransition(() => applyTheme(next)).finished.catch(() => void 0).finally(() => {
			delete root.dataset.themeTransition;
			for (const property of revealProperties) root.style.removeProperty(property);
			revealing = false;
		});
		return;
	}
	root.dataset.themeTransition = "fade";
	applyTheme(next);
	window.setTimeout(() => delete root.dataset.themeTransition, 260);
}
/** Explicit language choice (`null` when the browser language decides). */
function storedLocale() {
	return readPreference(localeCookie);
}
function rememberLocaleChoice(locale) {
	writePreference(localeCookie, locale);
}
/**
* The best supported language for this person: an explicit choice first, then the account's language, then
* the browser's preferred languages in order (the same list it sends as Accept-Language), then English.
* Detection is repeated on every visit; nothing is stored for it.
*/
function pickLocale(supported, { explicit = storedLocale(), account = null, browser } = {}) {
	const isSupported = (value) => !!value && supported.includes(value);
	if (isSupported(explicit)) return explicit;
	if (isSupported(account)) return account;
	const languages = browser ?? (typeof navigator === "undefined" ? [] : navigator.languages?.length ? navigator.languages : [navigator.language]);
	for (const tag of languages) {
		const base = tag?.toLowerCase().split("-")[0];
		if (isSupported(base)) return base;
	}
	return supported[0] ?? "en";
}
export { applyTheme, configurePreferences, pickLocale, rememberLocaleChoice, resolveTheme, sharedCookieDomain, storedLocale, storedTheme, transitionTheme };

//# sourceMappingURL=preferences.js.map